import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MeterRole } from '@core/models';
import { TopologicalDeviceTelemetryStore } from '@root/app/store/topological-device-telemetry.store';
import { ChartOptions } from 'chart.js';
import {
  ChartComponent,
  XemexChartDataset,
} from '@custom-components/general/chart/chart.component';
import { dayTooltipTitle } from '../day-hour-labels';
import {
  ChartBuilder,
  ChartType,
  toChartDatasets,
  toChartLabels,
  toChartNote,
  toChartBadge,
} from '../chartBuilder';
import {
  EnergyFlowBatteryIntervalModel,
  EnergyFlowEmeterIntervalModel,
  EnergyFlowIntervalModel,
} from '@core/models/topological-telemetry.model';

@Component({
  selector: 'xemex-emeter-insights',
  imports: [ChartComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './emeter.component.html',
})
export class EmeterComponent {
  telemetryStore = inject(TopologicalDeviceTelemetryStore);
  translateService = inject(TranslateService);
  selectedInsightContexts = input<Record<string, boolean>>({ grid: true });
  meterRole = input<MeterRole>('bidirectional');
  colors = {
    gridConsumed: '#16A085',
    gridDelivered: '#65DABC',
    heatpump: 'oklch(64.639% 0.22007 16.45)',
    heatpump_thermal_energy: 'oklch(74% 0.17 35)', // lighter, shifted toward orange — distinct but in the heatpump family
    heatpump_cop: 'oklch(50% 0.18 25)', // darker, deeper red — distinct from heatpump and thermal_energy but in the same family
    solarPanel: 'oklch(72.33% 0.1430 40.11)',
    solarForecastClearSky: 'oklch(82% 0.13 85)', // forecast, dashed — yellow, distinct from the solid solar line
    solarForecastWeather: 'oklch(70% 0.09 55)', // forecast, dotted — muted orange in the solar family
    battery_charged: 'oklch(52.53% 0.1354 219.94)',
    battery_discharged: 'oklch(86.137% 0.01232 255.14)',
    battery_soc: 'oklch(79.33% 0.0739 237.54)',
    evcharger: 'oklch(63.518% 0.06503 164.90)',
    additional_emeters: [
      { delivered: 'oklch(37.2% 0.044 257.287)', consumed: 'oklch(70.4% 0.04 256.788)' },
      { delivered: 'oklch(50.5% 0.213 27.518)', consumed: 'oklch(70.4% 0.191 22.216)' },
      { delivered: 'oklch(55.5% 0.163 48.998)', consumed: 'oklch(87.9% 0.169 91.605)' },
      { delivered: 'oklch(52% 0.105 223.128)', consumed: 'oklch(86.5% 0.127 207.078)' },
      { delivered: 'oklch(51.8% 0.253 323.949)', consumed: 'oklch(74% 0.238 322.16)' },
      { delivered: 'oklch(51.4% 0.222 16.935)', consumed: 'oklch(71.2% 0.194 13.428)' },
    ],
  };

  public chartOptions: ChartOptions = {
    maintainAspectRatio: false,
    interaction: { mode: 'nearest', intersect: false, axis: 'xy' },
    scales: {
      x: { grid: { display: false } },
      // No min: delivered energy is charted as negative, so the axis must span below 0.
      y: {},
      battery: {
        min: 0,
        max: 100,
        display: 'auto',
        ticks: {
          callback: (item: any) => `${item} %`,
        },
        grid: { display: false },
        position: 'right',
      },
      cop: {
        min: 0,
        display: 'auto',
        ticks: {
          callback: (item: any) => `${item}`,
        },
        grid: { display: false },
        position: 'right',
      },
    },
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        enabled: true,
        position: 'nearest',
        callbacks: {
          title(tooltipItems) {
            if (tooltipItems.length == 0) return '';
            const label = tooltipItems[0].label;
            return label.includes(':')
              ? dayTooltipTitle(tooltipItems[0].chart.data.labels ?? [], tooltipItems[0].dataIndex)
              : label;
          },
        },
      },
    },
  };

  private readonly _chartData = computed(() => {
    if (this.telemetryStore.isLoadingDeviceTelemetry()) return undefined;

    const chartBuilder = new ChartBuilder(this.telemetryStore, this.translateService);
    chartBuilder.withChartSetup();

    const grid_import_selector = (device: EnergyFlowIntervalModel) =>
      device.electricityGrid?.importedInWh == null
        ? null
        : device.electricityGrid.importedInWh / 1000;

    // Delivered (export) is charted below the x-axis as a negative value; consumed
    // (import) stays positive above it. A 'production' role meter's own output is the
    // exported reading, so it flips positive instead — the emeter-subchannel
    // export_selector below applies the same convention.
    const grid_export_selector = (device: EnergyFlowIntervalModel) => {
      if (device.electricityGrid?.exportedInWh == null) return null;
      const sign = this.meterRole() === 'production' ? 1 : -1;
      const value = (sign * device.electricityGrid.exportedInWh) / 1000;
      // A zero reading times sign -1 (or an already-signed-zero reading) yields JS's
      // -0, which chart tooltips/axes render as the string "-0" — normalize to +0.
      return value === 0 ? 0 : value;
    };

    const grid_selector = (
      device: EnergyFlowIntervalModel,
      energy_type_selector: (device: EnergyFlowIntervalModel) => number | null,
    ) =>
      device.electricityGrid?.quality && device.electricityGrid.quality !== 'missing'
        ? energy_type_selector(device)
        : null;

    const battery_selector = (
      battery: EnergyFlowBatteryIntervalModel,
      energy_type_selector: (battery: EnergyFlowBatteryIntervalModel) => number | null,
    ) => (battery.quality && battery.quality !== 'missing' ? energy_type_selector(battery) : null);

    if (this.selectedInsightContexts()['Grid']) {
      // Same role gating as the Emeters block below (T8): a 'consumption'/'production' role
      // meter only has one direction of data, so the other series is hidden entirely rather
      // than charted as an always-zero line.
      if (this.meterRole() !== 'production') {
        chartBuilder.withData(
          ChartType.LINE,
          (device: EnergyFlowIntervalModel) => grid_selector(device, grid_import_selector),
          'devices.detailTabs.insights.grid.consumed',
          this.colors.gridConsumed,
        );
      }
      if (this.meterRole() !== 'consumption') {
        chartBuilder.withData(
          ChartType.LINE,
          (device: EnergyFlowIntervalModel) => grid_selector(device, grid_export_selector),
          'devices.detailTabs.insights.grid.delivered',
          this.colors.gridDelivered,
        );
      }
    }

    if (this.selectedInsightContexts()['Warmtepomp']) {
      chartBuilder.withChannelData(
        ChartType.LINE,
        (d, c) => {
          const heatpump = d.heatPump.find(h => h.channelNumber == c);
          if (
            heatpump?.quality &&
            heatpump.quality !== 'missing' &&
            heatpump.energyUsedInWh != null
          )
            return heatpump.energyUsedInWh / 1000;
          return null;
        },
        i => i.heatPump,
        'devices.detailTabs.insights.heatpump.consumed',
        this.colors.heatpump,
      );
      chartBuilder.withChannelData(
        ChartType.LINE,
        (d, c) => {
          const heatpump = d.heatPump.find(h => h.channelNumber == c);
          if (
            heatpump?.quality &&
            heatpump.quality !== 'missing' &&
            heatpump.thermalEnergyInWh != null
          )
            return heatpump.thermalEnergyInWh / 1000;
          return null;
        },
        i => i.heatPump,
        'devices.detailTabs.insights.heatpump.thermal_energy',
        this.colors.heatpump_thermal_energy,
      );
      chartBuilder.withChannelData(
        ChartType.LINE,
        (d, c) => {
          const heatpump = d.heatPump.find(h => h.channelNumber == c);
          if (
            heatpump?.quality &&
            heatpump.quality !== 'missing' &&
            heatpump.coefficientOfPerformance != null
          )
            return heatpump.coefficientOfPerformance;
          return null;
        },
        i => i.heatPump,
        'devices.detailTabs.insights.heatpump.cop',
        this.colors.heatpump_cop,
        'cop',
      );
    }

    if (this.selectedInsightContexts()['SolarPanel']) {
      chartBuilder.withChannelData(
        ChartType.LINE,
        (d, c) => {
          const solarpanel = d.solarPanels.find(h => h.channelNumber == c);
          if (
            solarpanel?.quality &&
            solarpanel.quality !== 'missing' &&
            solarpanel.generatedInWh != null
          )
            return solarpanel.generatedInWh / 1000;
          return null;
        },
        i => i.solarPanels,
        'devices.detailTabs.insights.solar.generated',
        this.colors.solarPanel,
      );
      chartBuilder.withChannelData(
        ChartType.LINE,
        (d, c) => {
          const pv = d.pv?.find(p => p.channelNumber == c);
          if (pv?.quality && pv.quality !== 'missing' && pv.clearSkyInWh != null)
            return pv.clearSkyInWh / 1000;
          return null;
        },
        i => i.pv ?? [],
        'devices.detailTabs.insights.solar.forecastClearSky',
        this.colors.solarForecastClearSky,
        null,
        [6, 4],
      );
      chartBuilder.withChannelData(
        ChartType.LINE,
        (d, c) => {
          const pv = d.pv?.find(p => p.channelNumber == c);
          if (pv?.quality && pv.quality !== 'missing' && pv.weatherInWh != null)
            return pv.weatherInWh / 1000;
          return null;
        },
        i => i.pv ?? [],
        'devices.detailTabs.insights.solar.forecastWeather',
        this.colors.solarForecastWeather,
        null,
        [2, 3],
      );
    }

    if (this.selectedInsightContexts()['Battery']) {
      chartBuilder.withChannelData(
        ChartType.LINE,
        (d, c) => {
          const battery = d.battery.find(h => h.channelNumber == c);
          return battery?.chargedInWh != null
            ? battery_selector(battery, b => (b.chargedInWh ?? 0) / 1000)
            : null;
        },
        i => i.battery,
        'devices.detailTabs.insights.battery.charged',
        this.colors.battery_charged,
      );
      chartBuilder.withChannelData(
        ChartType.LINE,
        (d, c) => {
          const battery = d.battery.find(h => h.channelNumber == c);
          return battery?.chargedInWh != null
            ? battery_selector(battery, b => (b.dischargedInWh ?? 0) / 1000)
            : null;
        },
        i => i.battery,
        'devices.detailTabs.insights.battery.discharged',
        this.colors.battery_discharged,
      );
      chartBuilder.withChannelData(
        ChartType.LINE,
        (d, c) => {
          const battery = d.battery.find(h => h.channelNumber == c);
          return battery?.stateOfCharge != null
            ? battery_selector(battery, b => b.stateOfCharge)
            : null;
        },
        i => i.battery,
        'devices.detailTabs.insights.battery.soc',
        this.colors.battery_soc,
        'battery',
      );
    }

    if (this.selectedInsightContexts()['EVCharger']) {
      chartBuilder.withChannelData(
        ChartType.LINE,
        (d, c) => {
          const evCharger = d.evCharger.find(h => h.channelNumber == c);
          if (
            evCharger?.quality &&
            evCharger.quality !== 'missing' &&
            evCharger.chargedInWh != null
          )
            return evCharger.chargedInWh / 1000;
          return null;
        },
        i => i.evCharger,
        'devices.detailTabs.insights.evcharger.charged',
        this.colors.evcharger,
      );
    }

    const emeter_selector = (
      device: EnergyFlowIntervalModel,
      channelNumber: number,
      selector: (data: EnergyFlowEmeterIntervalModel) => number,
    ) => {
      const emeter = device.emeters.find(h => h.channelNumber == channelNumber);
      return emeter?.quality && emeter.quality !== 'missing' ? selector(emeter) : null;
    };

    // Delivered (export) charted as a negative value (below the x-axis), same convention
    // as grid_export_selector above — a 'production' role meter's only reading is its own
    // output, so it flips positive instead (RDT-832: a subchannel-only production meter,
    // e.g. a standalone solar CT clamp, was left permanently negative here even though the
    // main DLMS grid channel already got this flip via T5). A zero reading negates to JS's
    // -0, which chart tooltips/axes render as "-0" — normalize.
    const export_selector = (emeter: EnergyFlowEmeterIntervalModel) => {
      const sign = this.meterRole() === 'production' ? 1 : -1;
      const value = (sign * (emeter.exportedInWh ?? 0)) / 1000;
      return value === 0 ? 0 : value;
    };

    const import_selector = (emeter: EnergyFlowEmeterIntervalModel) =>
      (emeter.importedInWh ?? 0) / 1000;

    // Only prefix with the channel label (e.g. "emeter_0") once there's more than one
    // emeter channel to tell apart — a single channel just shows the plain type label.
    const label_selector = (
      device: EnergyFlowIntervalModel | undefined,
      channelNumber: number,
      channelCount: number,
      type: string,
    ) => {
      if (channelCount <= 1) return type;
      const emeter = device?.emeters.find(h => h.channelNumber == channelNumber);
      return (emeter?.label ?? '').concat(` ${type}`);
    };

    if (this.selectedInsightContexts()['Emeters']) {
      // Emeter subchannels now get the same production sign-flip as the main DLMS channel
      // (RDT-832, see export_selector above); a 'production'/'consumption' role meter also
      // hides the subchannel dataset it doesn't define (consumption/delivered respectively)
      // entirely.
      if (this.meterRole() !== 'production') {
        chartBuilder.withChannelData(
          ChartType.LINE,
          (device, channelNumber) => emeter_selector(device, channelNumber, import_selector),
          i => i.emeters,
          (device, channelNumber, channelCount) =>
            label_selector(
              device,
              channelNumber,
              channelCount,
              this.translateService.instant('devices.detailTabs.insights.consumed'),
            ),
          this.colors.additional_emeters.map(x => x.consumed),
        );
      }
      if (this.meterRole() !== 'consumption') {
        chartBuilder.withChannelData(
          ChartType.LINE,
          (device, channelNumber) => emeter_selector(device, channelNumber, export_selector),
          i => i.emeters,
          (device, channelNumber, channelCount) =>
            label_selector(
              device,
              channelNumber,
              channelCount,
              this.translateService.instant('devices.detailTabs.insights.delivered'),
            ),
          this.colors.additional_emeters.map(x => x.delivered),
        );
      }
    }

    // Energy on the primary axis adds up per hour, so the repeated 02:00 of the 25-hour day is stacked;
    // the state of charge and the COP are not amounts.
    return chartBuilder.build(
      dataset => !('yAxisID' in dataset) || (dataset.yAxisID ?? 'y') === 'y',
    );
  });

  chartLabels = computed<string[]>(() => toChartLabels(this._chartData()));
  chartNote = computed(() => toChartNote(this._chartData()));
  chartBadge = computed(() => toChartBadge(this._chartData()));

  chartDatasets = computed<XemexChartDataset[]>(() => toChartDatasets(this._chartData()));
}
